import { useTerminology } from '@/lib/useTerminology'
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listPositionRefs } from '../api/time-master-data.api';
import { ModeBanner } from '../components/ModeBanner';
import { useDataSourceMode } from '../hooks/useDataSourceMode';
import type { TimeProjectPositionRef, PaginatedResponse } from '../types/time-master-data.types';

/**
 * AP-09 / AP-13 — Liste der TimeProjectPositionRef-Datensätze inkl. CRUD-Aktionen.
 */
export default function TimeProjectPositionRefsPage() {
  // AP-16: Begriff des Mandanten (Projekt / Auftrag)
  const terms = useTerminology()
  const [search, setSearch] = useState('');
  const [data, setData] = useState<PaginatedResponse<TimeProjectPositionRef> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { canEdit } = useDataSourceMode('position');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listPositionRefs({ search: search || undefined, page: 1, limit: 50 })
      .then(res => { if (!cancelled) { setData(res); setError(null); } })
      .catch(e => { if (!cancelled) setError(e?.message ?? 'Fehler beim Laden'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [search]);

  const rows = useMemo(() => data?.data ?? [], [data]);

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{terms.positionPlural} (Zeiterfassung)</h1>
          <p className="text-sm text-muted-foreground">
            {`Hier gliederst du deine ${terms.projectPlural} feiner auf.`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Suchen (Name, externalId)"
            className="border rounded px-3 py-2 text-sm w-72"
          />
          {canEdit ? (
            <Link
              to="/time-tracking/positions/new"
              className="px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:opacity-90"
            >
              + Neu
            </Link>
          ) : (
            <button
              type="button"
              disabled
              title="Nicht verfügbar, weil diese Daten aus deinem anderen Programm kommen."
              className="px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground opacity-40 cursor-not-allowed"
            >
              + Neu
            </button>
          )}
        </div>
      </header>

      <ModeBanner kind="position" />

      {loading && <div className="text-sm text-muted-foreground">Wird geladen …</div>}
      {error && <div className="text-sm text-red-600">{error}</div>}

      {!loading && !error && (
        <div className="overflow-x-auto border rounded">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="text-left px-3 py-2">Nummer</th>
                <th className="text-left px-3 py-2">Bezeichnung</th>
                <th className="text-left px-3 py-2">{terms.project}-Ref</th>
                <th className="text-left px-3 py-2">Quelle</th>
                <th className="text-right px-3 py-2">Custom Fields</th>
                <th className="text-right px-3 py-2">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center px-3 py-6 text-muted-foreground">
                    Keine Datensätze.
                  </td>
                </tr>
              )}
              {rows.map(r => (
                <tr key={r.id} className="border-t">
                  <td className="px-3 py-2 font-mono">{r.nummer ?? '–'}</td>
                  <td className="px-3 py-2">
                    {r.name}
                    {r.description && <div className="text-xs text-muted-foreground">{r.description}</div>}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{r.projectRefId}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.externalSource
                      ? <span title={r.externalId ?? ''}>{r.externalSource} · {r.externalId}</span>
                      : <span className="text-muted-foreground">manuell</span>}
                  </td>
                  <td className="px-3 py-2 text-right text-xs text-muted-foreground">
                    {r.customFields ? Object.keys(r.customFields).length + ' Felder' : '–'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      to={`/time-tracking/positions/${r.id}/edit`}
                      className="text-xs border rounded px-2 py-1 hover:bg-accent"
                    >
                      Bearbeiten
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && (
        <div className="text-xs text-muted-foreground">
          Seite {data.meta.page} / {data.meta.totalPages} – insgesamt {data.meta.total} Einträge
        </div>
      )}
    </div>
  );
}
